import React, { useEffect, useMemo, useState } from "react";
import BottomSheet from "./BottomSheet";
import CountryFlag from "./CountryFlag";
import { SearchIcon } from "./connect/CountryPicker";
import { popularCountries, searchCountries } from "../lib/countryPicker";
import { FALLBACK_COUNTRIES, SupportedCountry, fetchCountryData } from "../lib/supportedCountries";
import { t } from "../i18n";
import "./connect/ConnectScreen.css";
import "./CountrySheet.css";

// "Your country" (founder, 2026-11-03): a profile's flag, picked or corrected.
// Nothing to do with the bank: this never touches where money goes. The same list
// and search as Connect your bank; every country can be picked here.

const CountrySheet: React.FC<{
  open: boolean;
  selectedCode?: string | null;
  onPick: (code: string) => void;
  onClose: () => void;
}> = ({ open, selectedCode, onPick, onClose }) => {
  const [countries, setCountries] = useState<SupportedCountry[]>(FALLBACK_COUNTRIES);
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (!open) return;
    setQuery("");
    fetchCountryData().then((d) => setCountries(d.countries)).catch(() => {});
  }, [open]);
  const results = useMemo(() => searchCountries(countries, query), [countries, query]);
  const popular = useMemo(() => popularCountries(countries, selectedCode || undefined), [countries, selectedCode]);
  const selected = countries.find((c) => c.code === selectedCode) || null;
  const row = (c: SupportedCountry, key: string) => (
    <button key={key} type="button" className={`cx-row${c.code === selectedCode ? " is-selected" : ""}`} onClick={() => onPick(c.code)}>
      <CountryFlag code={c.code} />
      <span className="cx-row-name">{c.name}</span>
    </button>
  );
  return (
    <BottomSheet open={open} title={t("countrySheet.yourCountry")} onClose={onClose}>
      <label className="cx-search csheet-search">
        <SearchIcon />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("connectBank.searchAllCountries")}
          aria-label={t("connectBank.searchAllCountries")}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
        />
      </label>
      {query.trim() ? (
        results.length ? results.map((c) => row(c, c.code)) : <p className="cx-empty">{t("connectBank.noCountryFound")}</p>
      ) : (
        <>
          {selected && row(selected, `sel-${selected.code}`)}
          {popular.length > 0 && <p className="cx-sec">{t("connectBank.popularCountries")}</p>}
          {popular.map((c) => row(c, `pop-${c.code}`))}
          <p className="cx-sec">{t("connectBank.allCountries")}</p>
          {countries.map((c) => row(c, c.code))}
        </>
      )}
    </BottomSheet>
  );
};

export default CountrySheet;
